import type {
  GameraPlayerReference,
  GameraTeamReference,
  GameraToken,
  Season,
  Game,
  BettingOdds,
  StatModel,
  GameraDomain,
} from './base'
import type {
  GameraDefaultResponse,
  NbaHistoricalBoxScore,
  NflHistoricalBoxScore,
  NhlHistoricalBoxScore,
  MlbHistoricalBoxScore,
  FcHistoricalBoxScore,
  GameraGenericGridsDetail,
  Visual,
} from './answer'
import { findLast, some, last, first } from 'lodash-es'
import type {
  CfbPlayersMediaQuery,
  NflPlayersMediaQuery,
} from '../../graphql/graphql'

export const ordinalNumber = (number: number) => {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const value = number % 100
  return (
    number + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0])
  )
}

export interface NbaBoxScoreVisual extends Visual {
  domain: 'NBA'
  detail: [NbaHistoricalBoxScore, GameraGenericGridsDetail]
}

export interface NflBoxScoreVisual extends Visual {
  domain: 'NFL'
  detail: [NflHistoricalBoxScore, GameraGenericGridsDetail]
}

export interface MlbBoxScoreVisual extends Visual {
  domain: 'MLB'
  detail: [MlbHistoricalBoxScore, GameraGenericGridsDetail]
}

export interface NhlBoxScoreVisual extends Visual {
  domain: 'NHL'
  detail: [NhlHistoricalBoxScore, GameraGenericGridsDetail]
}

export interface FcBoxScoreVisual extends Visual {
  domain: 'FC'
  detail: [FcHistoricalBoxScore, GameraGenericGridsDetail]
}

export interface GameraAnswerNbaBoxScore extends GameraDefaultResponse {
  domain: 'NBA'
  visual: NbaBoxScoreVisual
}
export interface GameraAnswerNflBoxScore extends GameraDefaultResponse {
  domain: 'NFL'
  visual: NflBoxScoreVisual
}
export interface GameraAnswerCfbBoxScore extends GameraDefaultResponse {
  domain: 'CFB'
  visual: NflBoxScoreVisual
}
export interface GameraAnswerMlbBoxScore extends GameraDefaultResponse {
  domain: 'MLB'
  visual: MlbBoxScoreVisual
}
export interface GameraAnswerNhlBoxScore extends GameraDefaultResponse {
  domain: 'NHL'
  visual: NhlBoxScoreVisual
}
export interface GameraAnswerFcBoxScore extends GameraDefaultResponse {
  domain: 'FC'
  visual: FcBoxScoreVisual
}

export type GameraAnswerBoxScore =
  | GameraAnswerNbaBoxScore
  | GameraAnswerNflBoxScore
  | GameraAnswerMlbBoxScore
  | GameraAnswerNhlBoxScore
  | GameraAnswerFcBoxScore

export interface GameraGamesResponse<G extends Game> {
  seasonYearDisplay: string
  teams: GameraTeamReference[]
  games: G[]
}

export type SplitStat =
  | GroupSplit<SplitStat>
  | PlayerSplit<SplitStat>
  | PeriodSplit<SplitStat>

interface BaseSplit<K extends SplitStat> {
  splitType: string
  statsLookupKey: string
  splits?: K[]
}

export interface GroupSplit<K extends SplitStat> extends BaseSplit<K> {
  splitType: 'group'
  groupIndex: number
}

export interface PlayerSplit<K extends SplitStat> extends BaseSplit<K> {
  splitType: 'player'
  playerId: number
}

export interface PeriodSplit<K extends SplitStat> extends BaseSplit<K> {
  splitType: 'period'
  period: number
}

export type MlbGameStatus =
  | 'scheduled'
  | 'postponed'
  | 'delayed'
  | 'inProgress'
  | 'suspended'
  | 'completed'
  | 'canceled'

export type Position =
  | 'pitcher'
  | 'catcher'
  | 'firstBase'
  | 'secondBase'
  | 'thirdBase'
  | 'shortStop'
  | 'leftField'
  | 'centerField'
  | 'rightField'
  | 'designatedHitter'
  | 'pinchHitter'
  | 'pinchRunner'

export const formatMlbPosition = (
  position?: Position | Position[],
  handedness?: 'left' | 'right',
) => {
  if (!position) return ''
  const lastPosition = Array.isArray(position)
    ? position[position.length - 1]
    : position

  switch (lastPosition) {
    case 'catcher':
      return 'C'
    case 'firstBase':
      return '1B'
    case 'secondBase':
      return '2B'
    case 'thirdBase':
      return '3B'
    case 'shortStop':
      return 'SS'
    case 'leftField':
      return 'LF'
    case 'centerField':
      return 'CF'
    case 'rightField':
      return 'RF'
    case 'designatedHitter':
      return 'DH'
    case 'pinchHitter':
      return 'PH'
    case 'pinchRunner':
      return 'PR'
    case 'pitcher':
      return handedness ? `${handedness === 'right' ? 'RH' : 'LH'}P` : 'P'
  }
}

export interface PlayerGameModel {
  playerId: number
  lineup: {
    positions?: Position[]
    battingOrder: number
    battingOrderSequence: number
    pitchingSequence: number
  }
}

export type MlbPitchingStatKey =
  | 'Pitching-CompleteGames'
  | 'Pitching-EarnedRunAverage'
  | 'Pitching-EarnedRuns'
  | 'Pitching-GamesPitched'
  | 'Pitching-GamesStarted'
  | 'Pitching-HitBatsmen'
  | 'Pitching-Hits'
  | 'Pitching-HomeRuns'
  | 'Pitching-InningsPitched'
  | 'Pitching-Losses'
  | 'Pitching-Runs'
  | 'Pitching-Saves'
  | 'Pitching-Shutouts'
  | 'Pitching-Strikeouts'
  | 'Pitching-Walks'
  | 'Pitching-Wins'

export type MlbBattingStatKey =
  | 'Batting-AtBats'
  | 'Batting-BattingAverage'
  | 'Batting-CaughtStealing'
  | 'Batting-Doubles'
  | 'Batting-HitByPitches'
  | 'Batting-Hits'
  | 'Batting-HomeRuns'
  | 'Batting-GamesPlayed'
  | 'Batting-OnBasePercentage'
  | 'Batting-Runs'
  | 'Batting-RunsBattedIn'
  | 'Batting-SluggingPercentage'
  | 'Batting-StolenBases'
  | 'Batting-Strikeouts'
  | 'Batting-Triples'
  | 'Batting-Walks'

export type MlbStatKey =
  | MlbPitchingStatKey
  | MlbBattingStatKey
  // Fielding
  | 'Fielding-Errors'
  // Batting
  | 'Batting-TotalBases'
  | 'Batting-TwoOutRunsBattedIn'
  | 'Batting-TwoOutRunnersLeftInScoringPosition'
  | 'Batting-IntentionalWalks'
  | 'Batting-HitByPitches'
  | 'Batting-SacrificeHits'
  | 'Batting-SacrificeFlies'
  | 'Batting-GroundedIntoDoublePlays'
  | 'Batting-AtBatsWithRunnersInScoringPosition'
  | 'Batting-HitsWithRunnersInScoringPosition'
  | 'Batting-LeftOnBaseAtEndOfInning'
  // Pitching
  | 'Pitching-WildPitches'
  | 'Pitching-Balks'
  | 'Pitching-PitchesThrown'
  | 'Pitching-StrikesThrown'
  | 'Pitching-GroundOuts'
  | 'Pitching-FlyOuts'
  | 'Pitching-TotalBattersFaced'
  | 'Pitching-InheritedRunners'
  | 'Pitching-InheritedRunnersScored'
  | 'Pitching-BlownSaves'
  | 'Pitching-Holds'

export type MlbStatKeySet = {
  pitchingStandard: MlbPitchingStatKey
  battingStandard: MlbBattingStatKey
}

export interface MlbTeamGameModel<K extends MlbStatKey> {
  teamId: number
  score: number
  record: {
    wins: number
    losses: number
  }
  gameResult: 'win' | 'loss' | 'noDecision' | 'suspended'
  stats?: {
    stats?: StatModel<K>
    splits?: {
      splitType?: string
      playerId: number
      stats?: StatModel<K>
    }[]
  }
  probablePitcher?: {
    playerId: number
  }
  lineScore?: {
    inning: number
    runs: number
  }[]
  players?: PlayerGameModel[]
}

interface MlbOfficial {
  assignment:
    | 'homePlate'
    | 'firstBase'
    | 'secondBase'
    | 'thirdBase'
    | 'leftField'
    | 'rightField'
  firstName?: string
  lastName?: string
  fullName?: string
}

export interface MlbGameDataResponse<K extends MlbStatKey> {
  gameId: number
  type: 'scheduled' | 'inProgress' | 'completed'
  gameStatus: MlbGameStatus
  seasonYearDisplay?: string
  season: Season
  gameDate: string
  gameTimestamp: string
  networkName: string
  homeTeam: MlbTeamGameModel<K>
  awayTeam: MlbTeamGameModel<K>
  weather?: {
    temperatureFahrenheit: number
    cloudCoveragePercentage: number
    wind: {
      speedMph: number
      direction?: string
    }
  }
  venue: {
    name?: string
    location?: string
  }
  timeOfDay: 'day' | 'night'
  officials?: MlbOfficial[]
  teams?: GameraTeamReference[]
  players?: GameraPlayerReference[]
  odds?: BettingOdds
  summary?: GameraToken[]
  hasPlayByPlay: boolean
}

export type Coordinates = {
  x: number
  y: number
}

interface HitData {
  hardness?: 'soft' | 'medium' | 'hard'
  trajectory?:
    | 'buntGrounder'
    | 'buntPopup'
    | 'buntLineDrive'
    | 'flyBall'
    | 'groundBall'
    | 'lineDrive'
    | 'popup'
  coordinates?: Coordinates
  launchAngle?: number
  launchSpeed?: number
  totalDistance?: number
}

interface PitchData {
  pitchType?:
    | 'fastball'
    | 'fourSeamFastball'
    | 'twoSeamFastball'
    | 'sinker'
    | 'cutter'
    | 'curveball'
    | 'slider'
    | 'sweeper'
    | 'slurve'
    | 'changeup'
    | 'knuckleball'
    | 'splitter'
    | 'screwball'
    | 'forkball'
    | 'eephus'
    | 'intentionalBall'
    | 'pitchOut'
    | 'automaticBall'
    | 'automaticStrike'
  zone?: number
  speed?: number
  spinDirection?: number
  spinRate?: number
  coordinates?: Coordinates
}

interface PitchCount {
  balls: number
  strikes: number
  outs: number
}

export interface Runner {
  outcomeType?:
    | 'steal'
    | 'caughtStealing'
    | 'pickoff'
    | 'otherAdvance'
    | 'otherOut'
  playerId: number
  startingBase: number
  endingBase: number
  description?: GameraToken[]
  isOut: boolean
}

interface PitchFlags {
  isAtBatOver: boolean
  isHit: boolean
  isBunt: boolean
  isWildPitch: boolean
  isPassedBall: boolean
  isDoublePlay: boolean
  isTriplePlay: boolean
}

export interface PitchAtBatEvent {
  type: 'pitch'
  pitchData?: PitchData
  hitData?: HitData
  count: PitchCount
  runners?: Runner[]
  outcomeType:
    | 'catcherInterference'
    | 'hitterInterference'
    | 'fieldersChoice'
    | 'outOfBattersBox'
    | 'outOnAppeal'
    | 'rulingPending'
    | 'single'
    | 'double'
    | 'triple'
    | 'homeRun'
    | 'hitByPitch'
    | 'balk'
    | 'intentionalWalk'
    | 'foul'
    | 'reachedOnError'
    | 'enforcedBall'
    | 'enforcedStrike'
    | 'ball'
    | 'dirtBall'
    | 'intentionalBall'
    | 'pitchout'
    | 'strikeSwinging'
    | 'strikeLooking'
    | 'sacrificeBunt'
    | 'sacrificeFly'
    | 'flyOut'
    | 'groundOut'
    | 'lineOut'
    | 'popOut'
  flags: PitchFlags
}

interface StealAtBatEvent {
  type: 'steal'
  count: PitchCount
  runners?: Runner[]
}

interface LineupChangeAtBatEvent {
  type: 'lineup'
  playerId: number
  teamId: number
  order?: number
  position: Position
  description?: GameraToken[]
}

type AtBatEvent = PitchAtBatEvent | StealAtBatEvent | LineupChangeAtBatEvent

export const isPitch = (event: AtBatEvent): event is PitchAtBatEvent =>
  event.type === 'pitch'
export const isSteal = (event: AtBatEvent): event is StealAtBatEvent =>
  event.type === 'steal'
export const isLineupChange = (
  event: AtBatEvent,
): event is LineupChangeAtBatEvent => event.type === 'lineup'

export interface InningAtBatEvent {
  pitcher: { playerId: number; handedness: 'right' | 'left' }
  batter: { playerId: number; handedness: 'right' | 'left' }
  events: AtBatEvent[]
  pitches?: PitchAtBatEvent[] // for scoring plays
  description?: GameraToken[]
  type: 'atBat'
}

interface InningLineupChangeEvent {
  playerId: number
  teamId: number
  order?: number
  position: Position
  description?: GameraToken[]
  type: 'lineup'
}

interface InningDesignatedRunnerEvent {
  playerId: number
  description?: GameraToken[]
  type: 'designatedRunner'
}

type InningEvent =
  | InningAtBatEvent
  | InningLineupChangeEvent
  | InningDesignatedRunnerEvent

export interface InningHalf {
  half: 'top' | 'bottom'
  events: InningEvent[]
}

export interface InningPlayByPlay {
  number: number
  halves: InningHalf[]
}

export interface MlbPlayByPlayResponse {
  gameId: number
  gameTimestamp: string
  gameStatus: MlbGameStatus
  homeTeam: Pick<GameraTeamReference, 'teamId'>
  awayTeam: Pick<GameraTeamReference, 'teamId'>
  innings: InningPlayByPlay[]
}

export const filterScoringPlays = (innings: InningPlayByPlay[]) => {
  return innings.map((inning) => {
    return {
      ...inning,
      halves: inning.halves.map((halfInning) => {
        return {
          ...halfInning,
          events: halfInning.events
            .filter((event) => event.type === 'atBat')
            .flatMap((event) => {
              const stealOrPitchEvents = event.events.filter(
                (e) =>
                  (e.type === 'steal' || e.type === 'pitch') &&
                  some(e.runners, (r) => r.endingBase === 4),
              )

              const lastPitch = last(stealOrPitchEvents)

              if (stealOrPitchEvents.length > 0) {
                return [
                  {
                    ...event,
                    events: stealOrPitchEvents,
                    pitches: event.events.filter((e) => e.type === 'pitch'),
                    description:
                      lastPitch?.type === 'pitch' && lastPitch.flags.isAtBatOver
                        ? event.description
                        : undefined,
                  },
                ]
              } else {
                return []
              }
            }),
        }
      }),
    }
  })
}

export const getCurrentInningNumber = (innings: InningPlayByPlay[]) => {
  return innings.length
}

export const getCurrentHalfInning = (innings: InningPlayByPlay[]) =>
  findLast(
    innings.flatMap((x) => x.halves),
    (x) => x.events.length > 0,
  )

export const getCurrentOuts = (innings: InningPlayByPlay[]) => {
  const inning = getCurrentHalfInning(innings)
  if (!inning) return 0
  const lastAtBat = findLast(
    inning.events,
    (e) => e.type === 'atBat' && e.events.length > 0,
  ) as InningAtBatEvent | undefined
  const lastPitch = last(
    lastAtBat?.events.filter((e) => e.type === 'pitch' || e.type === 'steal'),
  )
  return lastPitch?.count.outs ?? 0
}

export const getCurrentRunners = (innings: InningPlayByPlay[]) => {
  const inning = getCurrentHalfInning(innings)
  if (!inning) return []
  const lastAtBat = findLast(
    inning.events,
    (e) => e.type === 'atBat' && e.events.length > 0,
  ) as InningAtBatEvent | undefined
  const lastPitch = last(
    lastAtBat?.events.filter((e) => e.type === 'pitch' || e.type === 'steal'),
  )
  return lastPitch?.runners ?? []
}

export const getCurrentAtBat = (innings: InningPlayByPlay[]) => {
  const inning = getCurrentHalfInning(innings)
  if (!inning) return undefined
  return findLast(inning.events, (e) => e.type === 'atBat')
}

export const getCurrentAtBatCount = (innings: InningPlayByPlay[]) => {
  const atBat = getCurrentAtBat(innings)
  if (!atBat) return { balls: 0, strikes: 0 }
  const lastPitch = last(
    atBat.events.filter((e) => e.type === 'pitch' || e.type === 'steal'),
  )
  return lastPitch?.count ?? { balls: 0, strikes: 0 }
}

export const formatPlayOutcome = (pitch: PitchAtBatEvent) => {
  switch (pitch.outcomeType) {
    case 'ball':
    case 'dirtBall':
    case 'enforcedBall':
    case 'intentionalBall':
    case 'intentionalWalk':
      return 'Walk'
    case 'foul':
    case 'enforcedStrike':
    case 'strikeLooking':
    case 'strikeSwinging':
      return pitch.flags.isDoublePlay ? 'Strikeout Double Play' : 'Strikeout'
    case 'single':
      return 'Single'
    case 'double':
      return 'Double'
    case 'triple':
      return 'Triple'
    case 'homeRun':
      return 'Home Run'
    case 'sacrificeBunt':
      return 'Sacrifice Bunt'
    case 'sacrificeFly':
      return 'Sacrifice Fly'
    case 'groundOut':
      return pitch.flags.isDoublePlay
        ? 'Grounded Into DP'
        : pitch.flags.isTriplePlay
        ? 'Grounded Into TP'
        : 'Groundout'
    case 'lineOut':
    case 'flyOut':
      return pitch.flags.isDoublePlay
        ? 'Double Play'
        : pitch.flags.isTriplePlay
        ? 'Triple Play'
        : pitch.outcomeType.slice(0, 1).toUpperCase() +
          pitch.outcomeType.slice(1).toLowerCase()
    case 'popOut':
      return pitch.flags.isDoublePlay
        ? 'Double Play'
        : pitch.flags.isTriplePlay
        ? 'Triple Play'
        : 'Pop Out'
    case 'fieldersChoice':
      return 'Forceout'
    case 'hitByPitch':
      return 'Hit By Pitch'
    case 'reachedOnError':
      return 'Field Error'
    default:
      return pitch.outcomeType
  }
}

export const colorPlayOutcome = (pitch: PitchAtBatEvent) => {
  switch (pitch.outcomeType) {
    case 'ball':
    case 'dirtBall':
    case 'enforcedBall':
    case 'intentionalBall':
    case 'intentionalWalk':
    case 'pitchout':
      return 'fill-green bg-green'
    case 'foul':
    case 'enforcedStrike':
    case 'strikeLooking':
    case 'strikeSwinging':
      return 'fill-red bg-red'
    default:
      return 'fill-blue bg-blue'
  }
}

export const formatPitchType = (pitch: PitchAtBatEvent) => {
  switch (pitch.pitchData?.pitchType) {
    case 'twoSeamFastball':
      return '2-Seam Fastball'
    case 'fourSeamFastball':
      return '4-Seam Fastball'
    case 'automaticBall':
      return 'Automatic Ball'
    case 'automaticStrike':
      return 'Automatic Strike'
    case 'intentionalBall':
      return 'Intentional Ball'
    default:
      return pitch.pitchData?.pitchType ?? ''
  }
}

export const formatPitchOutcome = (pitch: PitchAtBatEvent) => {
  switch (pitch.outcomeType) {
    case 'catcherInterference':
      return 'Catcher Interference'
    case 'dirtBall':
      return 'Ball'
    case 'enforcedBall':
      return 'Enforced Ball'
    case 'enforcedStrike':
      return 'Enforced Strike'
    case 'pitchout':
      return 'Pitch Out'
    case 'fieldersChoice':
      return 'Forceout'
    case 'foul':
      return pitch.count.strikes === 3 ? 'Foul Tip' : 'Foul'
    case 'flyOut':
      return 'Flyout'
    case 'popOut':
      return 'Pop Out'
    case 'lineOut':
      return 'Lineout'
    case 'groundOut':
      return 'Groundout'
    case 'hitByPitch':
      return 'Hit By Pitch'
    case 'hitterInterference':
      return 'Hitter Interence'
    case 'homeRun':
      return 'Home Run'
    case 'intentionalBall':
      return 'Intentional Ball'
    case 'intentionalWalk':
      return 'Intentional Walk'
    case 'outOfBattersBox':
      return 'Out of Batters Box'
    case 'outOnAppeal':
      return 'Out On Appeal'
    case 'reachedOnError':
      return 'Reached On Error'
    case 'rulingPending':
      return 'In Play'
    case 'sacrificeBunt':
      return 'Sacrifice Bunt'
    case 'sacrificeFly':
      return 'Sacrifice Fly'
    case 'strikeLooking':
      return 'Strike Looking'
    case 'strikeSwinging':
      return 'Strike Swinging'
    default:
      return pitch.outcomeType ?? ''
  }
}

export interface MlbBoxGameState {
  status: MlbGameStatus
  lastInning?: InningPlayByPlay
  players?: GameraPlayerReference[]
  gameScore: {
    away: number
    home: number
  }
  lineup: {
    away?: PlayerGameModel[]
    home?: PlayerGameModel[]
  }
  stats: {
    away: MlbTeamGameModel<MlbStatKey>['stats']
    home: MlbTeamGameModel<MlbStatKey>['stats']
  }
  lineScore: {
    away: MlbTeamGameModel<MlbStatKey>['lineScore']
    home: MlbTeamGameModel<MlbStatKey>['lineScore']
  }
}

export interface MlbGameScore {
  gameId: number
  status: 'inProgress'
  gameDate: string
  domain: 'MLB'
  awayTeamId: number
  homeTeamId: number
  score: {
    away: number
    home: number
  }
  inningNumber: number
  inningHalf: 'top' | 'bottom'
  outs: number
  runners: Runner[]
}

export const mlbBoxGameState = (data: {
  gameData: MlbGameDataResponse<MlbStatKey>
  playByPlay: MlbPlayByPlayResponse
}): MlbBoxGameState => {
  const { gameData, playByPlay } = data
  return {
    status: gameData.gameStatus,
    lastInning: last(playByPlay.innings),
    players: gameData.players,
    gameScore: {
      away: gameData.awayTeam.score,
      home: gameData.homeTeam.score,
    },
    lineup: {
      away: gameData.awayTeam.players,
      home: gameData.homeTeam.players,
    },
    stats: {
      away: gameData.awayTeam.stats,
      home: gameData.homeTeam.stats,
    },
    lineScore: {
      away: gameData.awayTeam.lineScore,
      home: gameData.homeTeam.lineScore,
    },
  }
}

export const mlbGameScore = (data: {
  gameData?: MlbGameDataResponse<MlbStatKey>
  playByPlay?: MlbPlayByPlayResponse
}): MlbGameScore => {
  const { gameData, playByPlay } = data

  return {
    gameId: gameData?.gameId ?? 0,
    status: 'inProgress',
    gameDate: gameData?.gameDate ?? '',
    awayTeamId: gameData?.awayTeam.teamId ?? 0,
    homeTeamId: gameData?.homeTeam.teamId ?? 0,
    domain: 'MLB',
    score: {
      away: gameData?.awayTeam.score ?? 0,
      home: gameData?.homeTeam.score ?? 0,
    },
    inningHalf: getCurrentHalfInning(playByPlay?.innings ?? [])?.half ?? 'top',
    inningNumber: getCurrentInningNumber(playByPlay?.innings ?? []),
    outs: getCurrentOuts(playByPlay?.innings ?? []),
    runners: getCurrentRunners(playByPlay?.innings ?? []),
  }
}

export type NflGameStatus =
  | 'scheduled'
  | 'inProgress'
  | 'halftime'
  | 'completed'
  | 'postponed'
  | 'delayed'
  | 'canceled'

export type NflPassingStatKey =
  | 'Passing-Attempts'
  | 'Passing-Completions'
  | 'Passing-Yards'
  | 'Passing-YardsPerAttempt'
  | 'Passing-Touchdowns'
  | 'Passing-Interceptions'
  | 'Passing-Sacks'
  | 'Passing-SackYards'
  | 'Passing-Rating'

export type NflRushingStatKey =
  | 'Rushing-Attempts'
  | 'Rushing-Yards'
  | 'Rushing-YardsPerAttempt'
  | 'Rushing-Touchdowns'
  | 'Rushing-LongestRush'

export type NflReceivingStatKey =
  | 'Receiving-Targets'
  | 'Receiving-Receptions'
  | 'Receiving-Yards'
  | 'Receiving-YardsPerReception'
  | 'Receiving-Touchdowns'
  | 'Receiving-LongestReception'

export type NflFumblesStatKey = 'Fumbles' | 'FumblesLost' | 'FumbleRecoveries'

export type NflDefenseStatKey =
  | 'Defensive-Sacks'
  | 'Defensive-CombinedTackles'
  | 'Defensive-Tackles'
  | 'Defensive-TacklesForLoss'
  | 'Defensive-Interceptions'
  | 'Defensive-ForcedFumbles'
  | 'Defensive-InterceptionTouchdowns'

export type NflKickingStatKey =
  | 'Kicking-FieldGoalsAttempted'
  | 'Kicking-FieldGoalsMade'
  | 'Kicking-FieldGoalPercentage'
  | 'Kicking-ExtraPointsAttempted'
  | 'Kicking-ExtraPointsMade'
  | 'Kicking-ExtraPointPercentage'
  | 'Kicking-KickingPoints'
  | 'Kicking-LongestFieldGoal'

export type NflPuntingStatKey =
  | 'Punting-Punts'
  | 'Punting-Yards'
  | 'Punting-YardsAverage'
  | 'Punting-LongestPunt'

export type NflKickoffStatKey =
  | 'Kickoff-Returns'
  | 'Kickoff-ReturnYards'
  | 'Kickoff-ReturnYardsAverage'
  | 'Kickoff-LongestReturns'
  | 'Kickoff-ReturnTouchdowns'

export type NflPuntReturnStatKey =
  | 'PuntReturn-PuntReturns'
  | 'PuntReturn-Yards'
  | 'PuntReturn-YardsPerPuntReturn'
  | 'PuntReturn-LongestReturn'
  | 'PuntReturn-Touchdowns'

export type NflStatKey =
  | NflPassingStatKey
  | NflRushingStatKey
  | NflReceivingStatKey
  | NflFumblesStatKey
  | NflDefenseStatKey
  | NflKickingStatKey
  | NflPuntingStatKey
  | NflKickoffStatKey
  | NflPuntReturnStatKey
  | 'Penalties'
  | 'TimeOfPossession'
  | 'FirstDowns'
  | 'FirstDownPasses'
  | 'FirstDownRushes'
  | 'FirstDownPenalties'
  | 'ThirdDownAttempts'
  | 'ThirdDownConversions'
  | 'FourthDownAttempts'
  | 'FourthDownConversions'
  | 'RedZoneAttempts'
  | 'RedZoneConversions'

export type NflStatKeySet = {
  passingTotals: NflPassingStatKey
  rushingTotals: NflRushingStatKey
  receivingTotals: NflReceivingStatKey
  defenseTotals: NflDefenseStatKey
  kickoffReturnTotals: NflKickoffStatKey
  puntReturnTotals: NflPuntReturnStatKey
  kickingTotals: NflKickingStatKey
  puntingTotals: NflPuntingStatKey
  fumbleTotals: NflFumblesStatKey
}

interface NflSeason extends Season {
  week?: number
}

interface NflOfficial {
  assignment:
    | 'referee'
    | 'lineJudge'
    | 'fieldJudge'
    | 'backJudge'
    | 'sideJudge'
    | 'umpire'
    | 'replayOfficial'
    | 'downJudge'
  fullName?: string
  number?: number
}

export interface NflTeamGameModel<K extends NflStatKey> {
  teamId: number
  score: number
  usedTimeouts?: number
  record: {
    wins: number
    losses: number
    ties: number
  }
  gameResult: 'win' | 'loss' | 'tie'
  stats?: {
    team: {
      statsLookupKey: string
      statsLookup: Record<string, StatModel<K>>
    }
    player: {
      splits: GroupSplit<PlayerSplit<never>>[]
      statsLookupKey: string
      statsLookup: Record<string, StatModel<K>>
    }
  }
  lineScore?: {
    periods?: {
      period: number
      points: number
    }[]
  }
}

export interface NflGameDataResponse<K extends NflStatKey> {
  gameId: number
  type: 'scheduled' | 'inProgress' | 'completed'
  gameStatus: NflGameStatus
  seasonYearDisplay?: string
  season: NflSeason
  gameDate: string
  gameTimestamp: string
  networkName: string
  homeTeam: NflTeamGameModel<K>
  awayTeam: NflTeamGameModel<K>
  weather?: {
    condition:
      | 'sunny'
      | 'mostlySunny'
      | 'partlySunny'
      | 'clear'
      | 'mostlyClear'
      | 'cloudy'
      | 'mostlyCloudy'
      | 'partlyCloudy'
      | 'overcast'
      | 'rain'
      | 'lightRain'
      | 'thunderstorms'
      | 'mist'
      | 'snow'
      | 'lightSnow'
    temperatureFahrenheit: number
    humidityPercentage: number
    wind?: {
      speedMph: number
      direction?: string
    }
  }
  venue: {
    name?: string
    location?: string
    roofType: 'open' | 'retractable' | 'dome'
    surface: 'turf' | 'grass'
  }
  officials?: NflOfficial[]
  teams?: GameraTeamReference[]
  odds?: BettingOdds
  summary?: GameraToken[]
  hasPlayByPlay: boolean
}

export type NflDriveEndReason =
  | 'unknown'
  | 'touchdown'
  | 'safety'
  | 'fieldGoal'
  | 'missedFieldGoal'
  | 'muffedFieldGoal'
  | 'blockedFieldGoal'
  | 'blockedFieldGoalDowns'
  | 'blockedFieldGoalSafety'
  | 'punt'
  | 'blockedPunt'
  | 'blockedPuntDowns'
  | 'blockedPuntSafety'
  | 'downs'
  | 'interception'
  | 'fumble'
  | 'fumbleSafety'
  | 'endOfHalf'
  | 'endOfGame'

export interface NflDrive {
  driveSequence: number
  duration: string
  playCount: number
  flags: {
    isScoringDrive: boolean
  }
  gainYards: number
  endReason?: NflDriveEndReason
}

interface PlaySituation {
  clock: string
  down?: number
  yardsToFirst?: number
  possessionTeamId: number
  location: {
    teamId: number
    yardLine: number
  }
}

interface NflNonPlayEvent {
  type: 'nonPlay'
  nonPlayType:
    | 'timeout'
    | 'tvTimeout'
    | 'periodEnd'
    | 'twoMinuteWarning'
    | 'gameOver'
    | 'comment'
  eventSequence: number
  period: number
  driveSequence?: number
  clock: string
  description?: GameraToken[]
}

export interface NflPlayEvent {
  type: 'play'
  playType:
    | 'conversion'
    | 'extraPoint'
    | 'fieldGoal'
    | 'kickoff'
    | 'pass'
    | 'penalty'
    | 'punt'
    | 'rush'
    | 'freeKick'
    | 'play'
    | 'fairCatchKick'
  eventSequence: number
  period: number
  driveSequence: number
  startSituation: PlaySituation
  endSituation: PlaySituation
  homePoints: number
  awayPoints: number
  flags: {
    isScoringPlay: boolean
  }
  description?: GameraToken[]
  longSnapperPlayerId?: number
  holderPlayerId?: number
  kickingPlayerId?: number
  yardsAfterCatch?: number
  yardsAfterContact?: number
  kickBlockedPlayerId?: number
  fumbleForcedPlayerId?: number
}

export type NflEvent = NflNonPlayEvent | NflPlayEvent

export type NflScoringType =
  | 'defensiveInterceptionTouchdown'
  | 'defensiveTwoPointConversion'
  | 'blockedFieldGoalReturnTouchdown'
  | 'fieldGoalMade'
  | 'missedFieldGoalReturnTouchdown'
  | 'fumbleRecoveryTouchdown'
  | 'kickoffReturnTouchdown'
  | 'kickoffRecoveryTouchdown'
  | 'lateralTouchdown'
  | 'passingTouchdown'
  | 'blockedPuntReturnTouchdown'
  | 'puntReturnTouchdown'
  | 'rushingTouchdown'
  | 'safety'

export interface NflScoringEvent {
  scoreSequence: number
  eventSequence?: number
  period: number
  scoringTeamId: number
  scoringType: NflScoringType
  homePoints: number
  awayPoints: number
  description?: GameraToken[]
  touchdownPlayerId?: number
  touchdownPassingPlayerId?: number
  safetyPlayerId?: number
  fieldGoalKickingPlayerId?: number
  twoPointConversion?: {
    isSuccessful: boolean
    passingPlayerId?: number
    playerId?: number
  }
  extraPoint?: {
    isSuccessful: boolean
    kickingPlayerId?: number
  }
}

export interface NflPlayByPlayResponse {
  gameId: number
  gameDate: string
  gameTimestamp?: string
  gameStatus: NflGameStatus
  homeTeam: Pick<GameraTeamReference, 'teamId'>
  awayTeam: Pick<GameraTeamReference, 'teamId'>
  drives?: NflDrive[]
  events?: NflEvent[]
  scoringEvents?: NflScoringEvent[]
}

export interface NflBoxGameState {
  status: NflGameStatus
  lastDrive?: NflDrive
  lastEvents: NflEvent[]
  gameScore: {
    away: number
    home: number
  }
  playerStats: {
    away?: NonNullable<NflTeamGameModel<NflStatKey>['stats']>['player']
    home?: NonNullable<NflTeamGameModel<NflStatKey>['stats']>['player']
  }
  teamStats: {
    away?: NonNullable<NflTeamGameModel<NflStatKey>['stats']>['team']
    home?: NonNullable<NflTeamGameModel<NflStatKey>['stats']>['team']
  }
  scoringEvents: NflScoringEvent[]
  lineScore: {
    away: NflTeamGameModel<NflStatKey>['lineScore']
    home: NflTeamGameModel<NflStatKey>['lineScore']
  }
  usedTimeouts: {
    away: number
    home: number
  }
  players:
    | Record<
        string,
        NonNullable<NflPlayersMediaQuery['nfl']['players']>[number] | undefined
      >
    | Record<
        string,
        NonNullable<CfbPlayersMediaQuery['cfb']['players']>[number] | undefined
      >
}

export interface NflGameScore {
  gameId: number
  status: 'inProgress'
  gameDate: string
  domain: 'NFL' | 'CFB'
  awayTeamId: number
  homeTeamId: number
  score: {
    away: number
    home: number
  }
  quarter: number
  clock: string
  possessionTeamId: number
  location?: {
    teamId: number
    yardLine: number
  }
  redZone: boolean
  isHalftime: boolean
}

export const nflBoxGameState = (data: {
  gameData: NflGameDataResponse<NflStatKey>
  playByPlay: NflPlayByPlayResponse
  players:
    | Record<
        string,
        NonNullable<NflPlayersMediaQuery['nfl']['players']>[number] | undefined
      >
    | Record<
        string,
        NonNullable<CfbPlayersMediaQuery['cfb']['players']>[number] | undefined
      >
}): NflBoxGameState => {
  const { gameData, playByPlay, players } = data
  const lastDrive = last(playByPlay.drives)
  const lastEvents = playByPlay.events?.filter(
    (e) => e.driveSequence === lastDrive?.driveSequence,
  )
  return {
    status: gameData.gameStatus,
    lastDrive,
    lastEvents: lastEvents ?? [],
    gameScore: {
      away: gameData.awayTeam.score,
      home: gameData.homeTeam.score,
    },
    playerStats: {
      away: gameData.awayTeam.stats?.player,
      home: gameData.homeTeam.stats?.player,
    },
    teamStats: {
      away: gameData.awayTeam.stats?.team,
      home: gameData.homeTeam.stats?.team,
    },
    scoringEvents: playByPlay.scoringEvents ?? [],
    lineScore: {
      away: gameData.awayTeam.lineScore,
      home: gameData.homeTeam.lineScore,
    },
    usedTimeouts: {
      away: gameData.awayTeam.usedTimeouts ?? 0,
      home: gameData.homeTeam.usedTimeouts ?? 0,
    },
    players,
  }
}

export const nflGameScore = (data: {
  domain: 'NFL' | 'CFB'
  gameData?: NflGameDataResponse<NflStatKey>
  playByPlay?: NflPlayByPlayResponse
}): NflGameScore => {
  const { gameData, playByPlay, domain } = data

  const lastPlay = last(playByPlay?.events?.filter((e) => e.type === 'play'))
  const lastEvent = last(playByPlay?.events)

  return {
    gameId: gameData?.gameId ?? 0,
    status: 'inProgress',
    gameDate: gameData?.gameDate ?? '',
    domain,
    awayTeamId: gameData?.awayTeam.teamId ?? 0,
    homeTeamId: gameData?.homeTeam.teamId ?? 0,
    score: {
      away: gameData?.awayTeam.score ?? 0,
      home: gameData?.homeTeam.score ?? 0,
    },
    quarter: lastPlay?.period ?? 1,
    clock: lastPlay?.endSituation.clock ?? '15:00',
    possessionTeamId: lastPlay?.endSituation.possessionTeamId ?? 0,
    location: lastPlay?.endSituation.location,
    redZone:
      lastPlay?.endSituation.location.teamId !==
        lastPlay?.endSituation.possessionTeamId &&
      (lastPlay?.endSituation.location.yardLine ?? 100) <= 20,

    isHalftime:
      lastEvent?.type === 'nonPlay' &&
      lastEvent.nonPlayType === 'periodEnd' &&
      lastEvent.period === 2,
  }
}

export type NbaTotalsStatKey =
  | 'Assists'
  | 'Blocks'
  | 'DefensiveRebounds'
  | 'FieldGoalPercentage'
  | 'FieldGoalsAttempted'
  | 'FieldGoalsMade'
  | 'FreeThrowPercentage'
  | 'FreeThrowsAttempted'
  | 'FreeThrowsMade'
  | 'GamesPlayed'
  | 'MinutesPlayed'
  | 'OffensiveRebounds'
  | 'PersonalFouls'
  | 'Points'
  | 'Rebounds'
  | 'Steals'
  | 'ThreePointPercentage'
  | 'ThreePointsAttempted'
  | 'ThreePointsMade'
  | 'TrueShootingPercentage'
  | 'Turnovers'

export type NbaPerGameStatKey =
  | 'AssistsPerGame'
  | 'BlocksPerGame'
  | 'DefensiveReboundsPerGame'
  | 'FieldGoalPercentage'
  | 'FieldGoalsAttemptedPerGame'
  | 'FieldGoalsMadePerGame'
  | 'FreeThrowPercentage'
  | 'FreeThrowsAttemptedPerGame'
  | 'FreeThrowsMadePerGame'
  | 'GamesPlayed'
  | 'GamesStarted'
  | 'MinutesPlayedPerGame'
  | 'OffensiveReboundsPerGame'
  | 'PersonalFoulsPerGame'
  | 'PointsPerGame'
  | 'ReboundsPerGame'
  | 'StealsPerGame'
  | 'ThreePointPercentage'
  | 'ThreePointsAttemptedPerGame'
  | 'ThreePointsMadePerGame'
  | 'TrueShootingPercentage'
  | 'TurnoversPerGame'

export type NbaStatKey =
  | NbaPerGameStatKey
  | NbaTotalsStatKey
  | 'TechnicalFouls'
  | 'FlagrantFouls'
  | 'PointsInPaint'
  | 'PointsOffTurnovers'
  | 'SecondChancePoints'
  | 'FastBreakPoints'
  | 'GamesPlayed'
  | 'GamesStarted'
  | 'PlusMinus'
  | 'PlusMinusPerGame'

export type NbaStatKeySet = {
  perGame: NbaPerGameStatKey
  totals: NbaTotalsStatKey
}

export type NbaGameStatus =
  | 'scheduled'
  | 'inProgress'
  | 'halftime'
  | 'completed'
  | 'postponed'
  | 'delayed'
  | 'canceled'

export interface NbaSeason extends Omit<Season, 'type'> {
  type:
    | 'regularSeason'
    | 'postseason'
    | 'playoffs'
    | 'playInTournament'
    | 'inSeasonTournamentChampionship'
  nbaCupStage: 'group' // TODO: more types
}

interface NbaOfficial {
  assignment: 'headOfficial' | 'official'
  fullName?: string
  number?: number
}

export interface NbaTeamGameModel<K extends NbaStatKey> {
  teamId: number
  score: number
  record: {
    wins: number
    losses: number
  }
  gameResult: 'win' | 'loss'
  stats?: {
    team: {
      splits?: GroupSplit<PeriodSplit<never>>[]
      statsLookupKey: string
      statsLookup: Record<string, StatModel<K> | undefined>
    }
    player: {
      splits?: GroupSplit<PlayerSplit<PeriodSplit<never>>>[]
      statsLookupKey: string
      statsLookup: Record<string, StatModel<K> | undefined>
    }
  }
  lineScore?: {
    periods?: {
      period: number
      points: number
    }[]
  }
  players?: {
    playerId: number
    played: boolean
    notPlayedReason?: 'injuryIllness'
    notPlayedDescription?: string
    starter?: boolean
  }[]
  remainingTimeouts?: number
  inBonus?: boolean
}

export interface NbaGameDataResponse<K extends NbaStatKey> {
  gameId: number
  type: 'scheduled' | 'inProgress' | 'completed'
  gameStatus: NbaGameStatus
  season: NbaSeason
  gameDate: string
  gameTimestamp: string
  networkName: string
  awayTeam: NbaTeamGameModel<K>
  homeTeam: NbaTeamGameModel<K>
  venue: {
    name?: string
    location?: string
  }
  officials?: NbaOfficial[]
  odds?: BettingOdds
  summary?: GameraToken[]
}

type TurnoverType =
  | 'traveling'
  | 'badPass'
  | 'lostBall'
  | 'carrying'
  | 'palming'
  | 'outOfBounds'
  | 'doubleDribble'
  | 'offensiveGoaltending'
  | 'threeSecondViolation'
  | 'fiveSecondViolation'
  | 'eightSecondViolation'
  | 'tenSecondViolation'
  | 'backCourtViolation'
  | 'shotClockViolation'
  | 'laneViolation'
  | 'illegalAssist'
  | 'illegalScreen'
  | 'turnover'
  | 'tooManyPlayers'
  | 'excessTimeout'
  | 'basketFromBelow'
  | 'swingingElbows'
  | 'offensiveFoul'
  | 'punchedBall'
  | 'oppositeBasket'
  | 'jumpBallViolation'
  | 'inbound'
  | 'discontinueDribble'
  | 'fiveSecondViolationInbound'
  | 'kickedBallViolation'
  | 'fiveSecondViolationBackToTheBasket'

export const turnoverDescription = (turnoverType: TurnoverType | undefined) => {
  switch (turnoverType) {
    case 'traveling':
      return 'traveling turnover'
    case 'badPass':
      return 'bad pass turnover'
    case 'lostBall':
      return 'lost ball turnover'
    case 'carrying':
      return 'carrying turnover'
    case 'palming':
      return 'palming turnover'
    case 'outOfBounds':
      return 'out of bounds turnover'
    case 'doubleDribble':
      return 'double dribble turnover'
    case 'offensiveGoaltending':
      return 'offensive goaltending turnover'
    case 'threeSecondViolation':
      return 'three second violation turnover'
    case 'fiveSecondViolation':
      return 'five second violation turnover'
    case 'eightSecondViolation':
      return 'eight second violation turnover'
    case 'tenSecondViolation':
      return 'ten second violation turnover'
    case 'backCourtViolation':
      return 'back court violation turnover'
    case 'shotClockViolation':
      return 'shot clock violation turnover'
    case 'laneViolation':
      return 'lane violation turnover'
    case 'illegalAssist':
      return 'illegal assist turnover'
    case 'illegalScreen':
      return 'illegal screen turnover'
    case 'turnover':
      return 'turnover'
    case 'tooManyPlayers':
      return 'too many players turnover'
    case 'excessTimeout':
      return 'excess timeout turnover'
    case 'basketFromBelow':
      return 'basket from below turnover'
    case 'swingingElbows':
      return 'swinging elbows turnover'
    case 'offensiveFoul':
      return 'offensive foul turnover'
    case 'punchedBall':
      return 'punched ball turnover'
    case 'oppositeBasket':
      return 'opposite basket turnover'
    case 'jumpBallViolation':
      return 'jump ball violation turnover'
    case 'inbound':
      return 'inbound turnover'
    case 'discontinueDribble':
      return 'discontinue dribble turnover'
    case 'fiveSecondViolationInbound':
      return 'five second violation inbound turnover'
    case 'kickedBallViolation':
      return 'kicked ball violation turnover'
    case 'fiveSecondViolationBackToTheBasket':
      return 'five second violation back to the basket turnover'
    default:
      return 'turnover'
  }
}

type ShotType =
  | 'jumpShot'
  | 'jumpShotBank'
  | 'jumpShotDriving'
  | 'jumpShotDrivingBank'
  | 'jumpShotDrivingFloating'
  | 'jumpShotDrivingFloatingBank'
  | 'jumpShotTurnaround'
  | 'jumpShotTurnaroundBank'
  | 'jumpShotTurnaroundFadeaway'
  | 'jumpShotTurnaroundFadeawayBank'
  | 'jumpShotFloating'
  | 'jumpShotFadeaway'
  | 'jumpShotFadeawayBank'
  | 'jumpShotStepback'
  | 'jumpShotStepbackBank'
  | 'jumpShotPullup'
  | 'jumpShotPullupBank'
  | 'jumpShotRunning'
  | 'jumpShotRunningPullup'
  | 'layup'
  | 'layupAlleyOop'
  | 'layupCutting'
  | 'layupCuttingFingerRoll'
  | 'layupDriving'
  | 'layupDrivingReverse'
  | 'layupDrivingFingerRoll'
  | 'layupFingerRoll'
  | 'layupPutback'
  | 'layupReverse'
  | 'layupRunning'
  | 'layupRunningReverse'
  | 'layupRunningFingerRoll'
  | 'layupRunningAlleyOop'
  | 'layupTip'
  | 'dunk'
  | 'dunkAlleyOop'
  | 'dunkTip'
  | 'dunkDriving'
  | 'dunkDrivingReverse'
  | 'dunkPutback'
  | 'dunkRunning'
  | 'dunkReverse'
  | 'dunkRunningReverse'
  | 'dunkRunningAlleyOop'
  | 'dunkCutting'
  | 'tipShot'
  | 'hook'
  | 'hookBank'
  | 'hookDriving'
  | 'hookDrivingBank'
  | 'hookTurnaround'
  | 'hookTurnaroundBank'

export const getTwoPointFieldGoalShotTypeDescription = (
  shotType: ShotType | undefined,
) => {
  switch (shotType) {
    case 'jumpShot':
      return 'jump shot'
    case 'jumpShotBank':
      return 'bank shot'
    case 'jumpShotDriving':
      return 'driving jump shot'
    case 'jumpShotDrivingBank':
      return 'driving bank shot'
    case 'jumpShotDrivingFloating':
      return 'driving floater'
    case 'jumpShotDrivingFloatingBank':
      return 'driving bank floater'
    case 'jumpShotTurnaround':
      return 'turnaround jump shot'
    case 'jumpShotTurnaroundBank':
      return 'turnaround bank shot'
    case 'jumpShotTurnaroundFadeaway':
      return 'turnaround fadeaway'
    case 'jumpShotTurnaroundFadeawayBank':
      return 'turnaround bank fadeaway'
    case 'jumpShotFloating':
      return 'floater'
    case 'jumpShotFadeaway':
      return 'fadeaway'
    case 'jumpShotFadeawayBank':
      return 'bank fadeaway'
    case 'jumpShotStepback':
      return 'stepback'
    case 'jumpShotStepbackBank':
      return 'bank stepback'
    case 'jumpShotPullup':
      return 'pullup'
    case 'jumpShotPullupBank':
      return 'bank pullup'
    case 'jumpShotRunning':
      return 'running jump shot'
    case 'jumpShotRunningPullup':
      return 'running pullup'
    case 'layup':
      return 'layup'
    case 'layupAlleyOop':
      return 'alley-oop layup'
    case 'layupCutting':
      return 'cutting layup'
    case 'layupCuttingFingerRoll':
      return 'cutting finger roll'
    case 'layupDriving':
      return 'driving layup'
    case 'layupDrivingReverse':
      return 'driving reverse layup'
    case 'layupDrivingFingerRoll':
      return 'driving finger roll'
    case 'layupFingerRoll':
      return 'finger roll'
    case 'layupPutback':
      return 'putback layup'
    case 'layupReverse':
      return 'reverse layup'
    case 'layupRunning':
      return 'running layup'
    case 'layupRunningReverse':
      return 'running reverse layup'
    case 'layupRunningFingerRoll':
      return 'running finger roll'
    case 'layupRunningAlleyOop':
      return 'running alley-oop layup'
    case 'layupTip':
      return 'tip layup'
    case 'dunk':
      return 'dunk'
    case 'dunkAlleyOop':
      return 'alley-oop dunk'
    case 'dunkTip':
      return 'tip dunk'
    case 'tipShot':
      return 'tip'
    case 'dunkDriving':
      return 'driving dunk'
    case 'dunkDrivingReverse':
      return 'driving reverse dunk'
    case 'dunkPutback':
      return 'putback dunk'
    case 'dunkRunning':
      return 'running dunk'
    case 'dunkReverse':
      return 'reverse dunk'
    case 'dunkRunningReverse':
      return 'running reverse dunk'
    case 'dunkRunningAlleyOop':
      return 'running alley-oop dunk'
    case 'dunkCutting':
      return 'cutting dunk'
    case 'hook':
      return 'hook shot'
    case 'hookBank':
      return 'bank hook shot'
    case 'hookDriving':
      return 'driving hook shot'
    case 'hookDrivingBank':
      return 'driving bank hook shot'
    case 'hookTurnaround':
      return 'turnaround hook shot'
    case 'hookTurnaroundBank':
      return 'turnaround bank hook shot'
    default:
      return null
  }
}

export const getThreePointFieldGoalShotTypeDescription = (
  shotType: ShotType | undefined,
) => {
  switch (shotType) {
    case 'jumpShot':
      return 'three'
    case 'jumpShotBank':
      return 'bank three'
    case 'jumpShotDriving':
      return 'three'
    case 'jumpShotDrivingBank':
      return 'bank three'
    case 'jumpShotDrivingFloating':
      return '3pt floater'
    case 'jumpShotDrivingFloatingBank':
      return 'bank 3pt floater'
    case 'jumpShotTurnaround':
      return 'turnaround three'
    case 'jumpShotTurnaroundFadeaway':
      return 'turnaround fadeaway three'
    case 'jumpShotFloating':
      return '3pt floater'
    case 'jumpShotFadeaway':
      return 'fadeaway three'
    case 'jumpShotFadeawayBank':
      return 'fadeaway bank three'
    case 'jumpShotStepback':
      return 'stepback three'
    case 'jumpShotStepbackBank':
      return 'stepback bank three'
    case 'jumpShotPullup':
      return 'pullup three'
    case 'jumpShotPullupBank':
      return 'pullup bank three'
    case 'jumpShotRunning':
      return 'running three'
    case 'jumpShotRunningPullup':
      return 'running pullup three'
    default:
      return null
  }
}

export const shotTypeDescription = (
  shot: FieldGoalEvent | LegacyFieldGoalEvent,
) =>
  shot.isThreePoint && shot.type === 'fieldGoal'
    ? getThreePointFieldGoalShotTypeDescription(shot.shotType)
    : !shot.isThreePoint && shot.type === 'fieldGoal'
    ? getTwoPointFieldGoalShotTypeDescription(shot.shotType)
    : shot.type === 'legacyFieldGoal' && shot.isThreePoint
    ? '3PT shot'
    : '2PT shot'

type ReboundType = 'offensive' | 'defensive'

export type FoulType =
  | 'shooting'
  | 'offensive'
  | 'personal'
  | 'technical'
  | 'flagrantOne'
  | 'flagrantTwo'
  | 'clearPath'

export const foulDescription = (foul: FoulType | undefined) => {
  switch (foul) {
    case 'flagrantOne':
      return 'flagrant one'
    case 'flagrantTwo':
      return 'flagrant two'
    case 'clearPath':
      return 'clear path'
    default:
      return foul
  }
}

type ViolationType =
  | 'default'
  | 'defensiveGoaltending'
  | 'lane'
  | 'doubleLane'
  | 'kickedBall'
  | 'delayOfGame'
  | 'jumpBall'
  | 'defensiveThreeSeconds'

export const violationDescription = (x: ViolationType) => {
  switch (x) {
    case 'defensiveGoaltending':
      return 'defensive goaltending'
    case 'lane':
      return 'lane violation'
    case 'doubleLane':
      return 'double lane violation'
    case 'kickedBall':
      return 'kicked ball'
    case 'delayOfGame':
      return 'delay of game'
    case 'jumpBall':
      return 'jump ball violation'
    case 'defensiveThreeSeconds':
      return 'defensive three seconds'
    default:
      return 'violation'
  }
}

type ReviewType =
  | 'challenge'
  | 'challengeRulingUpheld'
  | 'challengeRulingOverturned'
  | 'challengeStands'
  | 'request'
  | 'requestRulingUpheld'
  | 'requestRulingOverturned'
  | 'requestStands'
  | 'instantReplay'
  | 'playReview'

export const reviewDesctiption = (x: ReviewType) => {
  switch (x) {
    case 'challengeRulingUpheld':
      return 'challenge ruling upheld'
    case 'challengeRulingOverturned':
      return 'challenge ruling overturned'
    case 'challengeStands':
      return 'challenge stands'
    case 'requestRulingUpheld':
      return 'request ruling upheld'
    case 'requestRulingOverturned':
      return 'request ruling overturned'
    case 'requestStands':
      return 'request stands'
    case 'instantReplay':
      return 'instant replay'
    case 'playReview':
      return 'play review'
    default:
      return x
  }
}

type StoppageType =
  | 'courtCleanup'
  | 'equipmentMalfunction'
  | 'bloodRule'
  | 'outOfBounds'
  | 'injury'
  | 'other'

export const stoppageDescription = (x: StoppageType) => {
  switch (x) {
    case 'bloodRule':
      return 'blood rule'
    case 'equipmentMalfunction':
      return 'equipment malfunction'
    case 'courtCleanup':
      return 'court cleanup'
    case 'outOfBounds':
      return 'out of bounds'
    default:
      return x
  }
}

interface NbaBaseEvent {
  period: number
  eventSequence: number
}

export interface LegacyFieldGoalEvent extends NbaBaseEvent {
  type: 'legacyFieldGoal'
  coordinates: Coordinates
  distanceFeet: number
  playerId?: number
  teamId: number
  isMade: boolean
  isThreePoint: boolean
}

export interface FieldGoalEvent extends NbaBaseEvent {
  type: 'fieldGoal'
  homePoints: number
  awayPoints: number
  clock: string
  coordinates: Coordinates
  shotType?: ShotType
  distanceFeet: number
  playerId?: number
  teamId: number
  assistPlayerId?: number
  isMade: boolean
  isThreePoint: boolean
}

export interface FreeThrowEvent extends NbaBaseEvent {
  type: 'freeThrow'
  homePoints: number
  awayPoints: number
  clock: string
  playerId?: number
  teamId: number
  isMade: boolean
  totalGranted: number
  attemptNumber: number
}

interface TurnoverEvent extends NbaBaseEvent {
  type: 'turnover'
  homePoints: number
  awayPoints: number
  clock: string
  turnoverPlayerId?: number
  stealPlayerId?: number
  turnoverTeamId: number
  turnoverType?: TurnoverType
}

interface ReboundEvent extends NbaBaseEvent {
  type: 'rebound'
  homePoints: number
  awayPoints: number
  clock: string
  playerId?: number
  teamId: number
  reboundType?: ReboundType
}

interface FoulEvent extends NbaBaseEvent {
  type: 'foul'
  homePoints: number
  awayPoints: number
  clock: string
  foulPlayerId?: number
  foulDrawnPlayerId?: number
  foulTeamId: number
  foulType?: FoulType
}

interface EjectionEvent extends NbaBaseEvent {
  type: 'ejection'
  homePoints: number
  awayPoints: number
  clock: string
  ejectionPlayerId?: number
  teamId: number
}

interface ViolationEvent extends NbaBaseEvent {
  type: 'violation'
  homePoints: number
  awayPoints: number
  clock: string
  teamId: number
  violationPlayerId?: number
  violationType: ViolationType
}

interface EndPeriodEvent extends NbaBaseEvent {
  type: 'endPeriod'
  homePoints: number
  awayPoints: number
}

interface ChallengeTimeoutEvent extends NbaBaseEvent {
  type: 'challengeTimeout'
  homePoints: number
  awayPoints: number
  clock: string
  teamId: number
}

interface JumpBallEvent extends NbaBaseEvent {
  type: 'jumpBall'
  homePoints: number
  awayPoints: number
  clock: string
  possessionTeamId: number
  homePlayerId?: number
  awayPlayerId?: number
  possessionPlayerId?: number
}

interface StoppageEvent extends NbaBaseEvent {
  type: 'stoppage'
  homePoints: number
  awayPoints: number
  clock: string
  stoppageType: StoppageType
}

interface TeamTimeoutEvent extends NbaBaseEvent {
  type: 'teamTimeout'
  homePoints: number
  awayPoints: number
  clock: string
  teamId: number
  timeoutSeconds: number
}

export interface LineupChangeEvent extends NbaBaseEvent {
  type: 'lineupChange'
  homePoints: number
  awayPoints: number
  clock: string
  teamId: number
  subbedOutPlayerIds?: number[]
  subbedInPlayerIds: number[]
  onCourtPlayerIds: number[]
}

interface OpenTipEvent extends NbaBaseEvent {
  type: 'openTip'
  homePoints: number
  awayPoints: number
  clock: string
  possessionTeamId: number
  homePlayerId?: number
  awayPlayerId?: number
  possessionPlayerId?: number
}

interface ReviewEvent extends NbaBaseEvent {
  type: 'review'
  homePoints: number
  awayPoints: number
  clock: string
  reviewType: ReviewType
}

export type NbaEvent =
  | LegacyFieldGoalEvent
  | FieldGoalEvent
  | FreeThrowEvent
  | TurnoverEvent
  | ReboundEvent
  | FoulEvent
  | EjectionEvent
  | ViolationEvent
  | EndPeriodEvent
  | ChallengeTimeoutEvent
  | JumpBallEvent
  | StoppageEvent
  | TeamTimeoutEvent
  | LineupChangeEvent
  | OpenTipEvent
  | ReviewEvent

export const getTeamIdFromNbaEvent = (play?: NbaEvent) => {
  switch (play?.type) {
    case 'challengeTimeout':
    case 'ejection':
    case 'fieldGoal':
    case 'legacyFieldGoal':
    case 'freeThrow':
    case 'lineupChange':
    case 'rebound':
    case 'violation':
    case 'teamTimeout':
      return play.teamId
    case 'turnover':
      return play.turnoverTeamId
    case 'openTip':
    case 'jumpBall':
      return play.possessionTeamId
    case 'foul':
      return play.foulTeamId
    case 'endPeriod':
    case 'review':
    case 'stoppage':
      return undefined
    default:
      return undefined
  }
}

export const getPlayerIdFromNbaEvent = (play?: NbaEvent) => {
  switch (play?.type) {
    case 'openTip':
    case 'jumpBall':
      return play.possessionPlayerId ?? 0
    case 'lineupChange':
      return play.subbedInPlayerIds[0] ?? 0
    case 'foul':
      return play.foulPlayerId ?? 0
    case 'ejection':
      return play.ejectionPlayerId ?? 0
    case 'violation':
      return play.violationPlayerId ?? 0
    case 'legacyFieldGoal':
    case 'fieldGoal':
    case 'freeThrow':
      return play.playerId ?? 0
    case 'turnover':
      return play.turnoverPlayerId ?? 0
    case 'rebound':
      return play.playerId ?? 0
    default:
      return 0
  }
}

export interface NbaPlayByPlayResponse {
  gameId: number
  gameDate: string
  gameTimestamp?: string
  gameStatus: NbaGameStatus
  homeTeam: Pick<GameraTeamReference, 'teamId'>
  awayTeam: Pick<GameraTeamReference, 'teamId'>
  events?: NbaEvent[]
}

export interface NbaBoxGameState {
  status: NbaGameStatus
  currentQuarter?: number
  lastEvents: NbaEvent[]
  gameScore: {
    away: number
    home: number
  }
  playerStats: {
    away?: NonNullable<NbaTeamGameModel<NbaStatKey>['stats']>['player']
    home?: NonNullable<NbaTeamGameModel<NbaStatKey>['stats']>['player']
  }
  teamStats: {
    away?: NonNullable<NbaTeamGameModel<NbaStatKey>['stats']>['team']
    home?: NonNullable<NbaTeamGameModel<NbaStatKey>['stats']>['team']
  }
  lineScore: {
    away: NbaTeamGameModel<NbaStatKey>['lineScore']
    home: NbaTeamGameModel<NbaStatKey>['lineScore']
  }
  remainingTimeouts: {
    away: number
    home: number
  }
  inBonus: {
    away: boolean
    home: boolean
  }
}

export interface NbaGameScore {
  domain: 'NBA' | 'WNBA'
  status: 'inProgress'
  gameDate: string
  gameId: number
  awayTeamId: number
  homeTeamId: number
  score: {
    away: number
    home: number
  }
  quarter: number
  clock: string
  isHalftime: boolean
}

export const nbaBoxGameState = (data: {
  gameData: NbaGameDataResponse<NbaStatKey>
  playByPlay: NbaPlayByPlayResponse
}): NbaBoxGameState => {
  const { gameData, playByPlay } = data
  const currentQuarter = last(playByPlay.events)?.period
  const lastEvents = playByPlay.events?.filter(
    (e) => e.period === currentQuarter,
  )
  return {
    status: gameData.gameStatus,
    currentQuarter,
    lastEvents: lastEvents ?? [],
    gameScore: {
      away: gameData.awayTeam.score,
      home: gameData.homeTeam.score,
    },
    playerStats: {
      away: gameData.awayTeam.stats?.player,
      home: gameData.homeTeam.stats?.player,
    },
    teamStats: {
      away: gameData.awayTeam.stats?.team,
      home: gameData.homeTeam.stats?.team,
    },
    lineScore: {
      away: gameData.awayTeam.lineScore,
      home: gameData.homeTeam.lineScore,
    },
    remainingTimeouts: {
      away: gameData.awayTeam.remainingTimeouts ?? 7,
      home: gameData.homeTeam.remainingTimeouts ?? 7,
    },
    inBonus: {
      away: gameData.awayTeam.inBonus ?? false,
      home: gameData.homeTeam.inBonus ?? false,
    },
  }
}

export const nbaGameScore = (data: {
  domain: 'NBA' | 'WNBA'
  gameData?: NbaGameDataResponse<NbaStatKey>
  playByPlay?: NbaPlayByPlayResponse
}): NbaGameScore => {
  const { gameData, playByPlay, domain } = data

  const lastEvent = last(
    playByPlay?.events?.filter((e) => e.type !== 'legacyFieldGoal'),
  )

  return {
    domain,
    status: 'inProgress',
    awayTeamId: gameData?.awayTeam.teamId ?? 0,
    homeTeamId: gameData?.homeTeam.teamId ?? 0,
    gameDate: gameData?.gameDate ?? '',
    gameId: gameData?.gameId ?? 0,
    score: {
      away: gameData?.awayTeam.score ?? 0,
      home: gameData?.homeTeam.score ?? 0,
    },
    quarter: lastEvent?.period ?? 1,
    clock:
      lastEvent?.type === 'endPeriod' ? '0:00' : lastEvent?.clock ?? '12:00',
    isHalftime: lastEvent?.type === 'endPeriod' && lastEvent.period === 2,
  }
}

export type NhlSkaterTotalsStatKey =
  | 'Assists'
  | 'BlockedShots'
  | 'Faceoff-Count'
  | 'Faceoff-WinPercentage'
  | 'Faceoff-Wins'
  | 'GameWinningGoals'
  | 'GamesPlayed'
  | 'Giveaways'
  | 'Goals'
  | 'Hits'
  | 'Overtime-Goals'
  | 'Penalty-Minutes'
  | 'PlusMinus'
  | 'Points'
  | 'PowerPlay-Assists'
  | 'PowerPlay-Goals'
  | 'ShootingPercentage'
  | 'ShortHanded-Assists'
  | 'ShortHanded-Goals'
  | 'Shots'
  | 'Takeaways'
  | 'TimeOnIcePerGame'

export type NhlGoalieTotalsStatKey =
  | 'GamesPlayed'
  | 'GamesStarted'
  | 'GoalsAgainst'
  | 'GoalsAgainstAverage'
  | 'Losses'
  | 'MinutesPlayed'
  | 'Overtime-Losses'
  | 'SavePercentage'
  | 'Saves'
  | 'ShotsAgainst'
  | 'Shutouts'
  | 'Wins'
  | 'Ties'

export type NhlStatKey =
  | NhlSkaterTotalsStatKey
  | NhlGoalieTotalsStatKey
  | 'TimeOnIce'
  | 'PowerPlay-Percentage'
  | 'PowerPlay-Opportunities'
  | 'Shootout-Goals'
  | 'Shootout-Attempts'

export type NhlStatKeySet = {
  skaterTotals: NhlSkaterTotalsStatKey
  goalieTotals: NhlGoalieTotalsStatKey
}

export type NhlGameStatus =
  | 'scheduled'
  | 'inProgress'
  | 'completed'
  | 'postponed'
  | 'delayed'
  | 'canceled'

interface NhlOfficial {
  assignment: 'headOfficial' | 'official'
  fullName?: string
  number?: number
}

export interface NhlTeamGameModel<K extends NhlStatKey> {
  teamId: number
  score?: number
  record: {
    wins: number
    losses: number
    ties: number
    overtimeLosses: number
  }
  gameResult: 'win' | 'loss' | 'tie' | 'overtimeLoss'
  stats?: {
    team?: {
      splits?: GroupSplit<PeriodSplit<never>>[]
      statsLookupKey: string
      statsLookup: Record<string, StatModel<K> | undefined>
    }
    player?: {
      splits?: GroupSplit<PlayerSplit<never>>[]
      statsLookupKey: string
      statsLookup: Record<string, StatModel<K> | undefined>
    }
  }
  lineScore: {
    periods?: {
      period: number
      points: number
    }[]
  }
  players?: { playerId: number; played: boolean }[]
}

export interface NhlGameDataResponse<K extends NhlStatKey> {
  gameId: number
  type: 'scheduled' | 'inProgress' | 'completed'
  gameStatus: NhlGameStatus
  season: Season
  gameDate: string
  gameTimestamp: string
  networkName: string
  awayTeam: NhlTeamGameModel<K>
  homeTeam: NhlTeamGameModel<K>
  venue: {
    name?: string
    location?: string
  }
  stars?: { playerId: number; teamId: number }[]
  officials?: NhlOfficial[]
  odds?: BettingOdds
  summary?: GameraToken[]
}

interface NhlBaseEvent {
  period: number
  eventSequence: number
  homeOnIcePlayerIds?: number[]
  awayOnIcePlayerIds?: number[]
  homeInPenaltyPlayerIds?: number[]
  awayInPenaltyPlayerIds?: number[]
  description?: GameraToken[]
}

type NhlShotType =
  | 'wrist'
  | 'slap'
  | 'backhand'
  | 'tipIn'
  | 'snap'
  | 'wrapAround'
  | 'bat'
  | 'cradle'
  | 'poke'
  | 'betweenLegs'
  | 'defelected'

type NhlShotContext = 'standard' | 'shootout' | 'penalty'

type NhlStrength = 'even' | 'powerPlay' | 'shortHanded'

type NhlShotMissedReason =
  | 'aboveCrossbar'
  | 'teammateBlocked'
  | 'wideLeft'
  | 'wideRight'
  | 'hitLeftPost'
  | 'hitRightPost'
  | 'hitCrossbar'
  | 'overNet'
  | 'goalPost'
  | 'wideOfNet'
  | 'deflected'
  | 'short'
  | 'highAndWideLeft'
  | 'highAndWideRight'

type NhlStoppageType =
  | 'offside'
  | 'icing'
  | 'clockProblem'
  | 'puckFrozen'
  | 'puckInCrowd'
  | 'goalieStoppedAfterShotOnGoal'
  | 'refereeOrLinesman'
  | 'highStick'
  | 'objectsOnIce'
  | 'handPass'
  | 'prematureSubstitution'
  | 'puckInNetting'
  | 'puckInBenches'
  | 'netDislodgedOffensiveSkater'
  | 'puckFrozenGoalieBeyondCenter'
  | 'skaterPuckFrozen'
  | 'netDislodgedDefensiveSkater'
  | 'goaliePuckFrozenPlayedBeyondCenter'
  | 'netDislodgedGoaltender'
  | 'goalieStopped'
  | 'puckInPenaltyBenches'
  | 'netOff'

type NhlPenaltyType =
  | 'benchPenalty'
  | 'boarding'
  | 'charging'
  | 'checkFromBehind'
  | 'clipping'
  | 'closingHandOnPuck'
  | 'crossChecking'
  | 'delayOfGame'
  | 'diving'
  | 'elbowing'
  | 'faceoffViolation'
  | 'fighting'
  | 'goalieInterference'
  | 'highSticking'
  | 'holding'
  | 'holdingStick'
  | 'hooking'
  | 'illegalCheckToHead'
  | 'illegalEquipment'
  | 'illegalSubstitution'
  | 'instigator'
  | 'interference'
  | 'interferenceDisplacingNet'
  | 'interferenceThrowingObject'
  | 'interferenceFromBench'
  | 'interferenceInCrease'
  | 'interferenceOnBreakaway'
  | 'kneeing'
  | 'misconduct'
  | 'playersLeavingBench'
  | 'roughing'
  | 'slashing'
  | 'spearing'
  | 'tooManyMenOnIce'
  | 'tripping'
  | 'unsportsmanlikeConduct'
  | 'unsportsmanlikeConductSittingOnBoards'
  | 'matchPenalty'

type NhlPenaltySeverity =
  | 'minor'
  | 'major'
  | 'misconduct'
  | 'doubleMinor'
  | 'penaltyShot'
  | 'match'

export interface GoalEvent extends NhlBaseEvent {
  type: 'goal'
  homePoints: number
  awayPoints: number
  clock: string
  coordinates?: Coordinates
  shotType?: NhlShotType
  playerId?: number
  goaliePlayerId?: number
  teamId: number
  assistPlayerId?: number[]
  isOwnGoal?: boolean
  isEmptyNetGoal?: boolean
  isAwardedGoal?: boolean
  shotContext: NhlShotContext
  strength?: NhlStrength
}

export interface ShotMissedEvent extends NhlBaseEvent {
  type: 'shotMissed'
  homePoints: number
  awayPoints: number
  clock: string
  coordinates?: Coordinates
  shotType?: NhlShotType
  missedReason?: NhlShotMissedReason
  playerId?: number
  goaliePlayerId?: number
  blockedPlayerId?: number
  teamId: number
  shotContext: NhlShotContext
  strength?: NhlStrength
}

export interface ShotSavedEvent extends Omit<ShotMissedEvent, 'type'> {
  type: 'shotSaved'
}

interface GiveawayEvent extends NhlBaseEvent {
  type: 'giveaway'
  homePoints: number
  awayPoints: number
  clock: string
  teamId: number
  playerId?: number
  coordinates?: Coordinates
  strength?: NhlStrength
}

interface TakeawayEvent extends Omit<GiveawayEvent, 'type'> {
  type: 'takeaway'
}

interface GoalieChangeEvent extends NhlBaseEvent {
  type: 'goalieChange'
  homePoints: number
  awayPoints: number
  clock: string
  teamId: number
  subbedInGoaliePlayerId?: number
  strength?: NhlStrength
}

interface EndPeriodEvent extends NhlBaseEvent {
  type: 'endPeriod'
  homePoints: number
  awayPoints: number
}

interface ChallengeEvent extends Omit<GoalieChangeEvent, 'type'> {
  type: 'challenge'
}

interface NhlStoppageEvent extends NhlBaseEvent {
  type: 'stoppage'
  homePoints: number
  awayPoints: number
  clock: string
  stoppageType?: NhlStoppageType
}

export interface PenaltyEvent extends NhlBaseEvent {
  type: 'penalty'
  homePoints: number
  awayPoints: number
  clock: string
  teamId: number
  penaltyType?: NhlPenaltyType
  severity?: NhlPenaltySeverity
  minutes?: number
  playerId?: number
  drawnPlayerId?: number
  servedPlayerId?: number
  coordinates?: Coordinates
  strength?: NhlStrength
}

export interface HitEvent extends NhlBaseEvent {
  type: 'hit'
  homePoints: number
  awayPoints: number
  clock: string
  teamId: number
  deliveredPlayerId?: number
  takenPlayerId?: number
  coordinates?: Coordinates
  strength?: NhlStrength
}

interface SubstitutionEvent extends NhlBaseEvent {
  type: 'substitution'
  homePoints: number
  awayPoints: number
  clock: string
  teamId: number
  subbedInPlayerIds: number[]
  strength?: NhlStrength
}

interface FaceoffEvent extends NhlBaseEvent {
  type: 'faceoff'
  homePoints: number
  awayPoints: number
  clock: string
  possessionTeamId: number
  winnerPlayerId?: number
  loserPlayerId?: number
  coordinates?: Coordinates
  strength?: NhlStrength
}

export type NhlEvent =
  | GoalEvent
  | ShotMissedEvent
  | ShotSavedEvent
  | GiveawayEvent
  | TakeawayEvent
  | GoalieChangeEvent
  | EndPeriodEvent
  | ChallengeEvent
  | NhlStoppageEvent
  | PenaltyEvent
  | HitEvent
  | FaceoffEvent
  | SubstitutionEvent

export const getTeamIdFromNhlEvent = (play?: NhlEvent) => {
  switch (play?.type) {
    case 'goal':
    case 'challenge':
    case 'giveaway':
    case 'takeaway':
    case 'penalty':
    case 'shotMissed':
    case 'shotSaved':
    case 'goalieChange':
    case 'substitution':
    case 'hit':
      return play.teamId
    case 'faceoff':
      return play.possessionTeamId
    case 'endPeriod':
    case 'stoppage':
      return undefined
    default:
      return undefined
  }
}

export interface NhlPlayByPlayResponse {
  gameId: number
  gameDate: string
  gameTimestamp: string
  gameStatus: NhlGameStatus
  homeTeam: Pick<GameraTeamReference, 'teamId'>
  awayTeam: Pick<GameraTeamReference, 'teamId'>
  events?: NhlEvent[]
}

export interface NhlBoxGameState {
  status: NhlGameStatus
  currentPeriod?: number
  lastEvents: NhlEvent[]
  gameScore: {
    away: number
    home: number
  }
  playerStats: {
    away?: NonNullable<NhlTeamGameModel<NhlStatKey>['stats']>['player']
    home?: NonNullable<NhlTeamGameModel<NhlStatKey>['stats']>['player']
  }
  teamStats: {
    away?: NonNullable<NhlTeamGameModel<NhlStatKey>['stats']>['team']
    home?: NonNullable<NhlTeamGameModel<NhlStatKey>['stats']>['team']
  }
  lineScore: {
    away: NhlTeamGameModel<NhlStatKey>['lineScore']
    home: NhlTeamGameModel<NhlStatKey>['lineScore']
  }
}

export const nhlBoxGameState = (data: {
  gameData: NhlGameDataResponse<NhlStatKey>
  playByPlay: NhlPlayByPlayResponse
}): NhlBoxGameState => {
  const { gameData, playByPlay } = data

  const currentPeriod = last(playByPlay.events)?.period
  const events =
    playByPlay.events?.filter((e) => e.period === currentPeriod) ?? []

  const shootoutScore = { away: 0, home: 0 }
  const awayId = gameData?.awayTeam.teamId
  const homeId = gameData?.homeTeam.teamId

  const lastEvents =
    currentPeriod === 5
      ? events.map((e) => ({
          ...e,
          awayShootoutScore:
            e.type === 'goal' && e.teamId === awayId
              ? (shootoutScore.away += 1)
              : shootoutScore.away,
          homeShootoutScore:
            e.type === 'goal' && e.teamId === homeId
              ? (shootoutScore.home += 1)
              : shootoutScore.home,
        }))
      : events

  return {
    status: gameData.gameStatus,
    currentPeriod,
    lastEvents,
    gameScore: {
      away: gameData.awayTeam.score ?? 0,
      home: gameData.homeTeam.score ?? 0,
    },
    playerStats: {
      away: gameData.awayTeam.stats?.player,
      home: gameData.homeTeam.stats?.player,
    },
    teamStats: {
      away: gameData.awayTeam.stats?.team,
      home: gameData.homeTeam.stats?.team,
    },
    lineScore: {
      away: gameData.awayTeam.lineScore,
      home: gameData.homeTeam.lineScore,
    },
  }
}

export interface NhlGameScore {
  domain: 'NHL'
  status: 'inProgress'
  gameDate: string
  gameId: number
  awayTeamId: number
  homeTeamId: number
  score: {
    away: number
    home: number
  }
  period: number
  clock: string
  endOfPeriod: boolean
  isShootout: boolean
  powerPlay: {
    away: boolean
    home: boolean
  }
}

export const nhlGameScore = (data: {
  gameData?: NhlGameDataResponse<NhlStatKey>
  playByPlay?: NhlPlayByPlayResponse
}): NhlGameScore => {
  const { gameData, playByPlay } = data

  const lastEvent = last(playByPlay?.events)

  const currentPeriod = lastEvent?.period
  const events =
    playByPlay?.events?.filter((e) => e.period === currentPeriod) ?? []
  const isShootout = first(events)?.clock === '00:00'

  const teamId = getTeamIdFromNhlEvent(lastEvent)

  return {
    domain: 'NHL',
    status: 'inProgress',
    gameDate: gameData?.gameDate ?? '',
    gameId: gameData?.gameId ?? 0,
    awayTeamId: gameData?.awayTeam.teamId ?? 0,
    homeTeamId: gameData?.homeTeam.teamId ?? 0,
    score: {
      away: gameData?.awayTeam.score ?? 0,
      home: gameData?.homeTeam.score ?? 0,
    },
    period: lastEvent?.period ?? 1,
    clock:
      lastEvent?.type === 'endPeriod' ? '0:00' : lastEvent?.clock ?? '20:00',
    endOfPeriod: lastEvent?.type === 'endPeriod',
    isShootout,
    powerPlay: {
      away:
        (lastEvent?.strength === 'powerPlay' &&
          teamId === gameData?.awayTeam.teamId) ||
        (lastEvent?.strength === 'shortHanded' &&
          teamId === gameData?.homeTeam.teamId),
      home:
        (lastEvent?.strength === 'powerPlay' &&
          teamId === gameData?.homeTeam.teamId) ||
        (lastEvent?.strength === 'shortHanded' &&
          teamId === gameData?.awayTeam.teamId),
    },
  }
}

export type FcStatKey = 'RedCards'

type FcGameStatus =
  | 'fixture'
  | 'playing'
  | 'break'
  | 'suspended'
  | 'postponed'
  | 'cancelled'
  | 'awarded'
  | 'played'

interface FcTeamGameModel<K extends FcStatKey> {
  teamId: number
  gameResult: 'win' | 'loss' | 'draw'
  score: number
  record: {
    wins: number
    losses: number
    draws: number
  }
  lineScore?: {
    periods: {
      type: 'regular' | 'extraTime' | 'penaltyShootout'
      score: number
    }[]
  }
  stats?: {
    team?: {
      statsKeyLookup?: string
      statsLookup?: Record<string, StatModel<K>>
    }
  }
}

export interface FcGameDataResponse<K extends FcStatKey> {
  type: 'completed' | 'inProgress' | 'scheduled'
  gameStatus: FcGameStatus
  gameId: number
  gameDate: string
  gameTimestamp: string
  season: Season
  awayTeam: FcTeamGameModel<K>
  homeTeam: FcTeamGameModel<K>
  state?: {
    minute: number
    extraMinute?: number
  }
}

export interface FcGameScore {
  gameId: number
  domain: 'FC'
  gameDate: string
  status: 'inProgress'
  gameStatus: FcGameStatus
  awayTeamId: number
  homeTeamId: number
  redCards: {
    away: number
    home: number
  }
  score: {
    away: number
    home: number
  }
  penaltyShootoutScore?: {
    away: number
    home: number
  }
  minute: number
  extraMinute?: number
}

export const fcGameScore = (data: {
  gameData?: FcGameDataResponse<FcStatKey>
}): FcGameScore => {
  const awayPenaltyShootout = data.gameData?.awayTeam.lineScore?.periods.find(
    (x) => x.type === 'penaltyShootout',
  )
  const homePenaltyShootout = data.gameData?.homeTeam.lineScore?.periods.find(
    (x) => x.type === 'penaltyShootout',
  )

  return {
    domain: 'FC',
    status: 'inProgress',
    gameStatus: data.gameData?.gameStatus ?? 'playing',
    gameId: data.gameData?.gameId ?? 0,
    gameDate: data.gameData?.gameDate ?? '',
    awayTeamId: data.gameData?.awayTeam.teamId ?? 0,
    homeTeamId: data.gameData?.homeTeam.teamId ?? 0,
    score: {
      away: data.gameData?.awayTeam.score ?? 0,
      home: data.gameData?.homeTeam.score ?? 0,
    },
    penaltyShootoutScore:
      awayPenaltyShootout || homePenaltyShootout
        ? {
            away: awayPenaltyShootout?.score ?? 0,
            home: homePenaltyShootout?.score ?? 0,
          }
        : undefined,
    redCards: {
      away:
        data.gameData?.awayTeam.stats?.team?.statsLookup?.['*'].RedCards
          ?.value ?? 0,
      home:
        data.gameData?.homeTeam.stats?.team?.statsLookup?.['*'].RedCards
          ?.value ?? 0,
    },
    minute: data.gameData?.state?.minute ?? 0,
    extraMinute: data.gameData?.state?.extraMinute,
  }
}

export const gameDataCompletedGame = (
  game:
    | NbaGameDataResponse<NbaStatKey>
    | MlbGameDataResponse<MlbStatKey>
    | NflGameDataResponse<NflStatKey>
    | NhlGameDataResponse<NhlStatKey>
    | FcGameDataResponse<FcStatKey>,
  domain: GameraDomain,
) => {
  return {
    id: game.gameId,
    gameId: game.gameId,
    domain,
    status: 'completed',
    gameDate: game.gameDate,
    awayTeam: {
      teamId: game.awayTeam.teamId,
      score: game.awayTeam.score ?? 0,
      gameResult: game.awayTeam.gameResult,
    },
    homeTeam: {
      teamId: game.homeTeam.teamId,
      score: game.homeTeam.score ?? 0,
      gameResult: game.homeTeam.gameResult,
    },
  }
}

export type StatKeySetByDomain = {
  NBA: NbaStatKeySet
  WNBA: NbaStatKeySet
  NFL: NflStatKeySet
  CFB: NflStatKeySet
  MLB: MlbStatKeySet
  NHL: NhlStatKeySet
  FC: never
}

export type StatKeyByDomain = {
  NBA: NbaStatKey
  WNBA: NbaStatKey
  NFL: NflStatKey
  CFB: NflStatKey
  MLB: MlbStatKey
  NHL: NhlStatKey
  FC: FcStatKey
}

export type GameDataResponseByDomain<
  K extends NbaStatKey | NflStatKey | MlbStatKey | FcStatKey,
> = {
  NBA: NbaGameDataResponse<Extract<K, NbaStatKey>>
  WNBA: NbaGameDataResponse<Extract<K, NbaStatKey>>
  NFL: NflGameDataResponse<Extract<K, NflStatKey>>
  CFB: NflGameDataResponse<Extract<K, NflStatKey>>
  MLB: MlbGameDataResponse<Extract<K, MlbStatKey>>
  NHL: NhlGameDataResponse<Extract<K, NhlStatKey>>
  FC: FcGameDataResponse<Extract<K, FcStatKey>>
}

export type PlayByPlayResponseByDomain = {
  NBA: NbaPlayByPlayResponse
  WNBA: NbaPlayByPlayResponse
  NFL: NflPlayByPlayResponse
  CFB: NflPlayByPlayResponse
  MLB: MlbPlayByPlayResponse
  NHL: NhlPlayByPlayResponse
}
